<template>
  <div>
    <custom-events-targeting-editor
      :selected-custom-events="selectedCustomEvents"
      :is-read-only="isReadOnly"
      :can-be-removed="canBeRemoved"
      @changed="onChanged"
      @cleared="clear" />
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import CustomEventsTargetingEditor from '@/common/components/controls/targeting/customEvents/CustomEventsTargetingEditor.vue';
  import EntityFormHelper from '@/common/components/entity-form/EntityFormHelper';
  import { Criterion } from '@/common/components/entity-form/EntityType';
  import TargetingSectionHelper, { ITargetingCriteria } from '@/common/components/entity-form/TargetingSectionHelper';
  import { LineItem, Plan } from '@/common/models';
  import TargetingCriterionSubType from '@/common/models/targeting-criteria/TargetingCriterionSubType';

  interface IProps {
    entity: LineItem | Plan;
  }

  const props = defineProps<IProps>();

  const emit = defineEmits(['changed', 'cleared']);

  const canBeRemoved = computed<boolean>(
    () => !isFromPackage.value || props.entity.packageInternal.customEventTargetingOverridable,
  );
  const entityFormHelper = computed<EntityFormHelper>(() => new EntityFormHelper(props.entity));
  const entityTargetingCriteria = computed<Criterion[]>(() => props.entity.targetingCriteria || []);
  const isFromPackage = computed<boolean>(() => entityFormHelper.value.isFromPackage);
  const isReadOnly = computed<boolean>(
    () =>
      isFromPackage.value &&
      !(props.entity as LineItem).isFromPreferredOrNGInternalDeal &&
      !(props.entity as LineItem).packageInternal.customEventTargetingOverridable,
  );
  const targetingCriteria = computed<ITargetingCriteria[]>(() => targetingSectionHelper.value.targetingCriteria || []);
  const targetingSectionHelper = computed<TargetingSectionHelper>(() => new TargetingSectionHelper(props.entity));
  const selectedCustomEvents = computed(() => {
    if (!props.entity) return [];

    const externalTargetingCriteria = targetingCriteria.value.filter((x) => x.isUpdatePushedExternally);
    const customEventCriteria = entityTargetingCriteria.value.filter((x) =>
      externalTargetingCriteria.some((etc) => etc.id === x.targetingCriterionID),
    );
    const selectedCustomEvents = externalTargetingCriteria
      .map((etc) => etc.subTypes)
      .flat()
      .filter((x) => customEventCriteria.some((y) => y.values.some((z) => z.code === x.code)));

    return selectedCustomEvents;
  });

  function updateEntityCriteria(value: Criterion[]): void {
    entityFormHelper.value.updateEntityCriteria(value);
  }

  function onChanged(customEvents: TargetingCriterionSubType[]): void {
    const externalTargetingCriteria = targetingCriteria.value.filter((x) => x.isUpdatePushedExternally);
    const otherTargetingCriteria = entityTargetingCriteria.value.filter(
      (x) => !externalTargetingCriteria.some((etc) => etc.id === x.targetingCriterionID),
    );
    const value = [
      ...(otherTargetingCriteria == null ? [] : otherTargetingCriteria),
      ...externalTargetingCriteria.map((etc) =>
        targetingSectionHelper.value.newEntityCriteria(
          props.entity.id,
          etc.id,
          customEvents.map((x) => ({
            code: x.code,
            value: 'DUMMY',
          })),
        ),
      ),
    ];

    updateEntityCriteria(value);
    emit('changed', customEvents);
  }

  function clear(): void {
    const destinationCriteriaIDs = targetingCriteria.value.filter((x) => x.type.includes('EVENT')).map((x) => x.id);
    const newEntityCriteria = entityTargetingCriteria.value.filter(
      (x) => !destinationCriteriaIDs.includes(x.targetingCriterionID),
    );
    updateEntityCriteria(newEntityCriteria);
    emit('cleared');
  }
</script>
